import React, { useContext, useEffect, useState } from 'react';
import UserContext from '../UserContext';
import MessageContext, { error, success } from '../MessageContext';
import { createEmailTemplate, deleteEmailTemplate, getDefaultEmailTemplate, getEmailsLog, getEmailTemplate, updateEmailTemplate } from '../rest/BackendApi';
import TabContext from '@mui/lab/TabContext';
import { Box, Button, Chip, Stack, Tab, TextField, Typography } from '@mui/material';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import EditIcon from '@mui/icons-material/Edit';
import Loading, { SimpleLoading } from '../components/Loading';
import Editor from '../components/Editor/Editor';
import EmailHistory from '../components/EmailHistory';
import { EMAIL_TAGS } from '../model/EmailTags';
import { isBlank, isBlankHtml } from '../util/StringUtil';
import { PageContainer, PageHeader, Section } from '../components/Page';
import ConfirmDialog from '../components/ConfirmDialog';

export const DEFAULT_TEMPLATE_NAME = 'My reminder template';
const TAG_TITLES = new Map(EMAIL_TAGS.map(tag => [tag.value, tag.title]));

/** Wrap {{tags}} in a highlighted span so the preview shows what gets filled in per client. */
export function highlightTags(html: string) {
  return html.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name: string) => {
    const title = TAG_TITLES.get(name);
    return `<span class="merge-tag"${title ? ` title="${title.replace(/"/g, '&quot;')}"` : ''}>${name}</span>`;
  });
}

const previewSx = {
  border: '1px solid #e0e0e0',
  borderRadius: 1,
  p: 2,
  backgroundColor: '#fff',
  fontSize: '0.9rem',
  lineHeight: 1.5,
  '.merge-tag': {
    backgroundColor: '#e3f2fd',
    color: '#0b5394',
    borderRadius: '4px',
    padding: '0 4px',
    fontSize: '0.85em',
    whiteSpace: 'nowrap',
  },
};

export default function EmailsPage() {
  const userContext = useContext(UserContext);
  const messageContext = useContext(MessageContext);

  const user = userContext?.user;
  const [emailLogs, setEmailLogs] = useState<EmailLog[] | null>(null);
  const [tab, setTab] = useState('1');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templateLoaded, setTemplateLoaded] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState<EmailTemplate | undefined>(undefined);
  const [defaultTemplate, setDefaultTemplate] = useState<{ subject: string; body: string } | undefined>(undefined);
  const [draft, setDraft] = useState<EmailTemplate | undefined>(undefined);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);

  const fetchEmailLogs = () => {
    setLoading(true);
    getEmailsLog()
      .then(data => {
        setEmailLogs(data || []);
      })
      .catch(() => {
        setEmailLogs([]);
        messageContext.setMessage(error('Failed to load email history'));
      })
      .finally(() => setLoading(false));
  };

  const fetchEmailTemplate = () => {
    setLoading(true);
    Promise.all([getEmailTemplate(), getDefaultEmailTemplate()])
      .then(([custom, defaults]) => {
        setEmailTemplate(custom || undefined);
        setDefaultTemplate(defaults);
      })
      .catch(() => messageContext.setMessage(error('Failed to load email template')))
      .finally(() => {
        setTemplateLoaded(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (tab == '1' && emailLogs == null) {
      fetchEmailLogs();
    } else if (tab == '2' && !templateLoaded) {
      fetchEmailTemplate();
    }
  }, [tab]);

  const startEditing = () => {
    // A new template starts from the default wording rather than a blank page.
    setDraft(
      emailTemplate ? { ...emailTemplate } : { name: DEFAULT_TEMPLATE_NAME, subject: defaultTemplate?.subject ?? '', body: defaultTemplate?.body ?? '' },
    );
  };

  const cancelEditing = () => setDraft(undefined);

  const updateDraft = (changes: Partial<EmailTemplate>) => setDraft(current => ({ ...(current as EmailTemplate), ...changes }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft) return;
    setSaving(true);
    try {
      if (draft.id) {
        setEmailTemplate(await updateEmailTemplate(draft));
        messageContext.setMessage(success('Template saved', { icon: 'save' }));
      } else {
        setEmailTemplate(await createEmailTemplate(draft));
        messageContext.setMessage(success('Custom template created', { icon: 'save' }));
      }
      setDraft(undefined);
    } catch (e) {
      // Keep the editor open so the changes aren't lost.
      messageContext.setMessage(error('Failed to save template'));
    }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!emailTemplate?.id) return;
    setSaving(true);
    try {
      await deleteEmailTemplate(emailTemplate.id);
      setEmailTemplate(undefined);
      messageContext.setMessage(success('Reset to the default BAS Ready template', { icon: 'undo' }));
    } catch (e) {
      messageContext.setMessage(error('Failed to reset template'));
    }
    setSaving(false);
  };

  const isCustom = !!emailTemplate?.id;

  const templateView = (
    <Section
      title={
        <Stack direction="row" alignItems="center" gap={1}>
          {isCustom ? emailTemplate?.name : 'Default template'}
          <Chip size="small" label={isCustom ? 'Custom' : 'BAS Ready default'} color={isCustom ? 'primary' : 'default'} variant="outlined" />
        </Stack>
      }
      description="Used for automatic reminders to clients you manage, and as the starting point when you send a reminder from the dashboard."
      actions={
        <>
          {isCustom && (
            <Button color="inherit" onClick={() => setConfirmResetOpen(true)}>
              Reset to default
            </Button>
          )}
          <Button variant="contained" startIcon={<EditIcon />} onClick={startEditing}>
            {isCustom ? 'Edit' : 'Customise'}
          </Button>
        </>
      }
    >
      <Typography variant="overline" color="text.secondary">
        Preview
      </Typography>
      <Box sx={previewSx}>
        <Box sx={{ pb: 1.5, mb: 1.5, borderBottom: '1px solid #eee' }}>
          <Typography component="span" variant="body2" color="text.secondary">
            Subject:{' '}
          </Typography>
          <b dangerouslySetInnerHTML={{ __html: highlightTags((emailTemplate ?? defaultTemplate)?.subject ?? '') }} />
        </Box>
        <div data-testid="template-body-preview" dangerouslySetInnerHTML={{ __html: highlightTags((emailTemplate ?? defaultTemplate)?.body ?? '') }} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Highlighted fields are filled in for each client when the email is sent.
      </Typography>
    </Section>
  );

  const templateEditor = draft && (
    <Section
      title={draft.id ? 'Edit template' : 'Create custom template'}
      description="Use the { } menu in the toolbar to insert details that change per client, like their name or unreconciled transactions."
    >
      <Box component="form" onSubmit={handleSave}>
        <TextField
          required
          label="Template name"
          size="small"
          value={draft.name || ''}
          onChange={e => updateDraft({ name: e.target.value })}
          fullWidth
          sx={{ mb: 2.5 }}
        />
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
          Subject
        </Typography>
        <Editor
          onContentUpdate={(subject: string) => updateDraft({ subject })}
          initialState={draft.subject || ''}
          isHtml={false}
          showToolbar={true}
          placeholder={'Start typing email subject...'}
          tags={EMAIL_TAGS}
        />
        <Typography variant="body2" sx={{ mt: 2.5, mb: 0.5, fontWeight: 600 }}>
          Body
        </Typography>
        <Editor
          onContentUpdate={(body: string) => updateDraft({ body })}
          initialState={draft.body || ''}
          isHtml={true}
          showToolbar={true}
          placeholder={'Start typing message body...'}
          tags={EMAIL_TAGS}
        />
        <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ mt: 2.5 }}>
          <Button color="inherit" onClick={cancelEditing}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" disabled={saving || isBlank(draft.name) || isBlankHtml(draft.body) || isBlank(draft.subject)}>
            Save template
          </Button>
        </Stack>
      </Box>
    </Section>
  );

  return (
    user && (
      <PageContainer maxWidth="none">
        {saving && <Loading />}
        <ConfirmDialog
          title="Reset to the default template?"
          open={confirmResetOpen}
          setOpen={setConfirmResetOpen}
          onConfirm={handleReset}
          cancelText="Cancel"
          confirmText="Reset"
        >
          Your custom template “{emailTemplate?.name}” will be deleted and reminders will use the BAS Ready default wording.
        </ConfirmDialog>

        <PageHeader title="Emails" description="Reminder emails sent to your clients, and the template they use." />

        <TabContext value={tab}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList onChange={(_, value: string) => setTab(value)} aria-label="Emails sections">
              <Tab label="History" value="1" />
              <Tab label="Reminder template" value="2" />
            </TabList>
          </Box>
          <TabPanel value="1" sx={{ px: 0 }}>
            <EmailHistory emailLogs={emailLogs} loading={loading} onRefresh={fetchEmailLogs} />
          </TabPanel>
          <TabPanel value="2" sx={{ px: 0, maxWidth: 860 }}>
            {loading || !templateLoaded ? <SimpleLoading message="Loading template" /> : draft ? templateEditor : templateView}
          </TabPanel>
        </TabContext>
      </PageContainer>
    )
  );
}
